import { CustomDimensionsService, CustomEventsService } from "@piwikpro/ngx-piwik-pro";
import { filter } from "rxjs";

import { Injectable, inject } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";

import { ENV } from "../app/app.runtime";
import { PIWIK_DIMENSION } from "../app/constants";
import { AnalyticsIdentity, AnalyticsModule } from "./analytics.types";

/**
 * Single facade over the Piwik Pro tracking services. Sets the customer and module custom
 * dimensions and emits feature-usage events.
 *
 * All methods no-op unless `ENV.PIWIK_SITEID` is configured, so tracking calls can be
 * placed freely throughout the app without guarding at every call site.
 *
 * Feature events currently emitted (category = module, an `AnalyticsModule`):
 *
 * | Feature                        | Module                | Action               | Name                        | Trigger                |
 * |--------------------------------|-----------------------|----------------------|-----------------------------|------------------------|
 * | SLS retrieval                  | `network`             | `sls-retrieval`      | —                           | button click           |
 * | Line-chart date inspection     | current module        | `inspect-line-chart` | `date-change`               | debounced (500ms)      |
 * | Line-chart service inspection  | current module        | `inspect-line-chart` | `service-change`            | dropdown selection     |
 * | Self-service usage             | current module        | `self-service`       | dialog/action type          | on dialog open         |
 * | CBW export                     | `cert`                | `cbw-export`         | asset type                  | button click (success) |
 * | SURFcert log inspection        | `cert`                | `inspect-cert-log`   | `<view>:<interaction>`      | filter/sort/paginate   |
 * | SURFwireless ticket inspection | `wireless`            | `inspect-ticket`     | scope (location/organisation)| on open               |
 * | Service-disruption ticket      | `service-disruptions` | `inspect-ticket`     | ticket category             | on expand              |
 *
 * SURFcert log `<view>` is `security-scan` or `dns-firewall`; `<interaction>` is `filter`,
 * `sort`, or `paginate` (e.g. `security-scan:filter`).
 *
 * "Current module" means the module is derived from the current route via
 * {@link AnalyticsService.trackCurrentFeature}, for components that appear in multiple subapps.
 */
@Injectable({
  providedIn: "root",
})
export class AnalyticsService {
  private readonly dimensions = inject(CustomDimensionsService);
  private readonly events = inject(CustomEventsService);
  private readonly router = inject(Router);

  private get enabled(): boolean {
    return !!ENV.PIWIK_SITEID;
  }

  constructor() {
    if (!this.enabled) return;

    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => this.setModule(moduleForUrl(event.urlAfterRedirects)));
  }

  /** Sets the Session-scoped customer dimensions for the current visit. */
  setIdentity(identity: AnalyticsIdentity): void {
    if (!this.enabled) return;

    this.dimensions.setCustomDimensionValue(PIWIK_DIMENSION.CUSTOMER_NAME, identity.customerName);
    this.dimensions.setCustomDimensionValue(PIWIK_DIMENSION.CUSTOMER_ID, identity.customerId);
  }

  /** Sets the Event-scoped module dimension. Called automatically on navigation. */
  setModule(module: AnalyticsModule): void {
    if (!this.enabled) return;

    this.dimensions.setCustomDimensionValue(PIWIK_DIMENSION.MODULE, module);
  }

  /** Emits a feature-usage event attributed to the given module. */
  trackFeature(module: AnalyticsModule, action: string, name?: string, value?: number): void {
    if (!this.enabled) return;

    this.events.trackEvent(module, action, name, value);
  }

  /**
   * Emits a feature-usage event whose module is derived from the current route. Use from
   * shared components (charts, self-service dialogs) that can appear in multiple subapps.
   */
  trackCurrentFeature(action: string, name?: string, value?: number): void {
    if (!this.enabled) return;

    this.trackFeature(moduleForUrl(this.router.url), action, name, value);
  }
}

/**
 * Maps a route URL to its {@link AnalyticsModule}. Order matters: specific prefixes are
 * checked before the network-service fallback.
 */
export function moduleForUrl(url: string): AnalyticsModule {
  const path = url.split(/[?#]/, 1)[0];

  if (path.startsWith("/news")) return AnalyticsModule.News;
  if (path.startsWith("/domains")) return AnalyticsModule.Domeinen;
  if (path.startsWith("/cert")) return AnalyticsModule.Cert;
  if (path.startsWith("/storing-en-onderhoud")) return AnalyticsModule.ServiceDisruptions;
  if (path.startsWith("/diensten/Wireless")) {
    return AnalyticsModule.Wireless;
  }

  return AnalyticsModule.Network;
}
